import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  debounce,
  FormControlLabel,
  IconButton,
  Slider,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { palette } from "../../themes/theme";
import { useDispatch, useSelector } from "react-redux";
import PrimaryTextField from "../PrimaryTextField";
import {
  setActiveMarker,
  updateTradeArea,
  updateTradeAreas,
} from "../../redux/slices/mapSlice";
import { CustomDraggable } from "../Draggable";
import { useCallback, useEffect, useState } from "react";
import * as turf from "@turf/turf";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ColorPicker from "../ColorPicker";
import { RadiusRingSelector } from "./RadiusRingSelector";
import { fetchEmojiIcons } from "../../api";
import { CustomTextBoxClass } from "./utilities/CustomTextbox";

export const MarkerStyleSetting = ({
  markers,
  setMarkers,
  markerRef,
  mapRef,
  isEnabledActionRef,
  drawRef,
  createTextBox,
  setTextBox,
}) => {
  const activeMarker = useSelector((state) =>
    state.map.tradeAreas.find((d) => d.id === state.map.activeMarker)
  );
  const allTradeAreas = useSelector((state) => state.map.tradeAreas);
  const dispatch = useDispatch();

  const [selectedValue, setSelectedValue] = useState({});
  const [markerName, setMarkerName] = useState(activeMarker.name);
  const [enableRadiusRing, setEnableRadiusRing] = useState(
    Boolean(activeMarker.radiusEnable)
  );
  const [loading, setLoading] = useState(false);
  const [activeEmoji, setActiveEmoji] = useState("");
  const [iconNames, setIconNames] = useState([
    { key: "fa-solid fa-apartment" },
    { key: "fa-solid fa-house" },
    { key: "fa-solid fa-hotel" },
    { key: "fa-solid fa-hospital" },
    { key: "fa-solid fa-school" },
    { key: "fa-sharp fa-solid fa-database" },
    { key: "fa-solid fa-building" },
    { key: "fa-solid fa-utensils" },
    { key: "fa-sharp fa-solid fa-industry-windows" },
    { key: "fa-solid fa-gas-pump" },
    { key: "fa-solid fa-dumbbell" },
    { key: "fa-duotone fa-solid fa-traffic-light-stop" },
    { key: "fa-solid fa-tree" },
    { key: "fa-sharp fa-solid fa-triangle-person-digging" },
    { key: "fa-solid fa-truck" },
    { key: "fa-solid fa-cars" },
    { key: "fa-solid fa-bicycle" },
    { key: "fa-solid fa-tractor" },
    { key: "fa-solid fa-cart-shopping" },
    { key: "fa-solid fa-person" },
    { key: "fa-solid fa-campfire" },
    { key: "fa-solid fa-xmark" },
    { key: "fa-solid fa-check" },
    { key: "fa-solid fa-circle-parking" },
    { key: "fa-solid fa-landmark-dome" },
  ]);
  const [showUpdateShading, setShowUpdateShading] = useState(-1);
  const [radiusColorStyle, setRadiusColorStyle] = useState({});

  const selectedMarker = markers.find((d) => d.id === activeMarker.id);
  const allowRadiiUpdate = activeMarker.radiusSetting.length;

  const updateChanges = (data) => {
    const updatedTradeAreas = allTradeAreas.map((d) =>
      d.id === data.id ? data : d
    );
    dispatch(updateTradeAreas(updatedTradeAreas));
  };

  const handleNameChange = (e) => {
    const newActiveMarker = JSON.parse(JSON.stringify(activeMarker));
    newActiveMarker.name = e.target.value;
    updateChanges(newActiveMarker);
  };

  const handleColorChange = (value) => {
    if (activeMarker.pointer.image) {
      selectedMarker.marker.getElement().querySelector("i").style.color = value;
    } else {
      selectedMarker.marker
        .getElement()
        .querySelector("svg")
        .querySelector("path")
        .setAttribute("fill", value);
    }

    markerRef.current = markers.map((d) =>
      d.id === activeMarker.id ? selectedMarker : d
    );
    setMarkers(markerRef.current);
    const newActiveMarker = JSON.parse(JSON.stringify(activeMarker));
    newActiveMarker.pointer.color = value;
    updateChanges(newActiveMarker);
  };

  const handleSizeChange = (value) => {
    // if (value > 30 || value < 14) return;
    if (activeMarker.pointer.image) {
      selectedMarker.marker
        .getElement()
        .querySelector("i").style.fontSize = `${value}px`;
    } else {
      selectedMarker.marker
        .getElement()
        .querySelector("svg")
        .setAttribute("height", value + 11);
    }

    markerRef.current = markers.map((d) =>
      d.id === activeMarker.id ? selectedMarker : d
    );
    setMarkers(markerRef.current);
    const newActiveMarker = JSON.parse(JSON.stringify(activeMarker));
    newActiveMarker.pointer.size = value;
    updateChanges(newActiveMarker);
  };

  const onClose = () => {
    dispatch(setActiveMarker(null));
  };

  const labelMarkerAdded = async ({
    coordinates,
    defaultText,
    inputMarkerId,
    markerValue,
  }) => {
    const mapCoordinates = { lat: coordinates[0], lng: coordinates[1] };
    isEnabledActionRef.current = "radiusSetting";
    if (Number(markerValue.val) > 0) {
      // setResetMap(false);
      await createTextBox({
        coordinates: mapCoordinates,
        previousValue: "",
        markerId: inputMarkerId,
        defaultText,
        currentMarkerId: inputMarkerId,
      });
    } else {
      CustomTextBoxClass.DeleteTextBox({
        inputMarkerId,
        mapDraw: drawRef.current,
        setTextBox,
        // setUndoHistory,
        // setRedoHistory,
        setDrawnFeaturesSupport: () => {},
        isDelete: false,
        map: mapRef,
        markerRef,
      });
      setMarkers(markerRef.current);
    }
  };

  const labelMarker = ({ coordinates, type, val, id, index }) => {
    const driveTimeText =
      "Drive Time - " +
      parseInt(val) +
      (parseFloat(val) <= 1 ? " Minute" : " Minutes");
    const radiusText = `${val} Mile`.replace("0.", ".");

    const markerCoordinates = coordinates.features
      ? coordinates.features[0].geometry.coordinates[0]
      : coordinates.geometry.coordinates[0];
    let southernmostPoint = markerCoordinates[0];

    markerCoordinates.forEach((coord) => {
      if (coord[1] < southernmostPoint[1]) {
        southernmostPoint = coord;
      }
    });
    let markerLatLng = [0, 0];
    if (type === "radius") {
      const parsedVal = parseFloat(val);
      // Calculate the 90% south latitude
      const lat90 =
        southernmostPoint[1] +
        (parsedVal < 0.5
          ? 1.2
          : parsedVal < 1
          ? 1.12
          : parsedVal < 2
          ? 1.1
          : parsedVal < 45
          ? 1.08
          : 0.97) *
          (markerCoordinates[0][1] - southernmostPoint[1]);

      // Calculate the average longitude for placement
      const lngAverage =
        markerCoordinates.reduce((acc, coord) => acc + coord[0], 0) /
        markerCoordinates.length;
      markerLatLng = [lat90, lngAverage];
    } else {
      if (index === 0 || index === 1) {
        const latSorted = [...markerCoordinates].sort((a, b) => a[1] - b[1]);
        const southIndex = Math.floor(latSorted.length * 0.6);
        const northIndex = Math.ceil(latSorted.length * 0.4);
        if (index === 0) markerLatLng = latSorted[southIndex];
        else markerLatLng = latSorted[northIndex];
      } else if (index === 2 || index === 3) {
        // Sort coordinates by longitude for east and west points
        const lonSorted = [...markerCoordinates].sort((a, b) => a[0] - b[0]);
        const westIndex = Math.floor(lonSorted.length * 0.6);
        const eastIndex = Math.ceil(lonSorted.length * 0.4);
        if (index === 2) markerLatLng = lonSorted[westIndex];
        else markerLatLng = lonSorted[eastIndex];
      }
    }
    const updatedMarkerLatLng = [markerLatLng[1], markerLatLng[0]];
    const markerValue = { type, val };
    labelMarkerAdded({
      coordinates: type === "radius" ? markerLatLng : updatedMarkerLatLng,
      defaultText: type === "radius" ? radiusText : driveTimeText,
      inputMarkerId: id,
      markerValue,
    });
  };

  const addRingRadiusLayer = useCallback(
    ({
      name,
      coordinates,
      setting,
      radius,
      type,
      index,
      // tradeAreaId: string,
      render = true,
    }) => {
      const circleLayer = mapRef.getLayer(name);
      if (type && render)
        labelMarker({ coordinates, type, val: radius, id: name, index });
      if (!circleLayer) {
        mapRef.addSource(name, {
          type: "geojson",
          data: coordinates,
        });
        mapRef.addLayer({
          id: name,
          type: "fill",
          source: name,
          layout: {},
          paint: {
            "fill-color": setting.isTransparent
              ? "rgba(0, 0, 0, 0)"
              : setting.defaultRingRadiusColor,
            "fill-opacity": parseFloat(setting.defaultOpacity),
          },
        });
        mapRef.addLayer({
          id: `${name}-line`,
          type: "line",
          source: name,
          layout: {},
          paint: {
            "line-color": setting.isBorderTransparent
              ? "rgba(0, 0, 0, 0)"
              : setting.defaultRingRadiusBorder,
            "line-width": parseInt(setting.defaultBorderWidth),
          },
        });
      } else {
        mapRef.getSource(name).setData(coordinates);
        mapRef.setPaintProperty(
          name,
          "fill-color",
          setting.isTransparent
            ? "rgba(0, 0, 0, 0)"
            : setting.defaultRingRadiusColor
        );
        mapRef.setPaintProperty(
          name,
          "fill-opacity",
          parseFloat(setting.defaultOpacity)
        );
        mapRef.setPaintProperty(
          name + "-line",
          "line-color",
          setting.isBorderTransparent
            ? "rgba(0, 0, 0, 0)"
            : setting.defaultRingRadiusBorder
        );
        mapRef.setPaintProperty(
          name + "-line",
          "line-width",
          parseInt(setting.defaultBorderWidth)
        );
        mapRef.setLayoutProperty(name, "visibility", "visible");
        mapRef.setLayoutProperty(name + "-line", "visibility", "visible");
      }
      // setTradeAreaId(tradeAreaId);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const removeDriveTime = useCallback((name) => {
    const markerToBeRemoved = markerRef.current.find(
      (item) => item.id === name
    );
    if (markerToBeRemoved) {
      markerToBeRemoved.marker.remove();
    }
    markerRef.current = markerRef.current.filter((item) => item.id !== name);

    setMarkers(markerRef.current);
    const circleLayer = mapRef.getLayer(name);
    if (circleLayer) {
      mapRef.removeLayer(name + "-line");
      mapRef.removeLayer(name);
      mapRef.removeSource(name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleRingRadius = async (_, checked) => {
    const newActiveMarker = JSON.parse(JSON.stringify(activeMarker));
    newActiveMarker.radiusEnable = checked;
    updateChanges(newActiveMarker);
    setEnableRadiusRing(checked);
    dispatch(
      updateTradeArea({
        id: activeMarker.id,
        value: {
          radiusEnable: checked,
        },
      })
    );
    const data = {};
    for (let i = 3; i >= 0; --i) {
      if (checked && i === 3) {
        dispatch(
          updateTradeArea({
            id: activeMarker.id,
            value: {
              radiusSetting: {
                order: i,
                type: "",
              },
            },
          })
        );
        continue;
      }
      const radius = checked ? activeMarker.radiusSetting[i].defaultRadius : 0;
      const lngLat = [activeMarker.latlng.lng, activeMarker.latlng.lat];
      const points = turf.featureCollection([]);
      points.features.push(turf.point(lngLat));
      const circle = turf.circle(turf.point(lngLat), parseFloat(radius), {
        units: "miles",
      });
      dispatch(
        updateTradeArea({
          id: activeMarker.id,
          value: {
            radiusSetting: {
              order: i,
              radiusCoordinates: circle,
              type: "radius-ring",
            },
          },
        })
      );
      addRingRadiusLayer({
        name: `circleData-${i}-${lngLat[0]}-${lngLat[1]}`,
        coordinates: circle,
        setting: activeMarker.radiusSetting[i],
        radius,
        type: "radius",
        index: i,
      });
      data[i] = { type: "radius-ring", size: radius };
    }
    setSelectedValue(data);
    if (checked)
      mapRef.easeTo({
        center: [activeMarker.latlng.lng, activeMarker.latlng.lat],
        zoom: 14,
        duration: 2000,
      });
  };

  const handleStyleChange = (key, val) => {
    if (key === "pointerColor") return handleColorChange(val);
    if (key === "size") return handleSizeChange(val);
  };

  const handleEmojiChange = (key) => {
    const ii = document.createElement("i");
    ii.id = `emoji-${key}`;
    ii.style.fontSize = "30px";
    ii.className = key;
    ii.style.color = activeMarker.pointer.color;
    ii.style.textShadow = "0px 2px 2px black";

    selectedMarker.marker
      .getElement()
      .replaceChild(ii, selectedMarker.marker.getElement().firstChild);
    markerRef.current = markers.map((d) =>
      d.id === activeMarker.id ? selectedMarker : d
    );
    setMarkers(markerRef.current);
    const newActiveMarker = JSON.parse(JSON.stringify(activeMarker));
    newActiveMarker.pointer.image = key;
    updateChanges(newActiveMarker);
    setActiveEmoji(key);
  };

  const changeStyle = (type, event) => {
    const tempStyle = { ...radiusColorStyle };
    const layerId = tempStyle.name;
    const circleLayer = mapRef.getLayer(layerId);
    if (!circleLayer) return;
    let colorKey, color;
    switch (type) {
      case "background-color":
        tempStyle.defaultRingRadiusColor = event;
        mapRef.setPaintProperty(layerId, "fill-color", event);
        color = event;
        colorKey = "defaultRingRadiusColor";
        break;
      case "background-color-transparent":
        tempStyle.isTransparent = !event.target.checked;
        mapRef.setPaintProperty(
          layerId,
          "fill-color",
          tempStyle.isTransparent
            ? "rgba(0, 0, 0, 0)"
            : tempStyle.defaultRingRadiusColor
        );
        color = tempStyle.isTransparent;
        colorKey = "isTransparent";
        break;
      case "border-color":
        tempStyle.defaultRingRadiusBorder = event;
        mapRef.setPaintProperty(layerId + "-line", "line-color", event);
        color = event;
        colorKey = "defaultRingRadiusBorder";
        break;
      case "border-color-transparent":
        tempStyle.isBorderFilled = !event.target.checked;
        mapRef.setPaintProperty(
          layerId + "-line",
          "line-color",
          tempStyle.isBorderFilled
            ? "rgba(0, 0, 0, 0)"
            : tempStyle.defaultRingRadiusBorder
        );
        color = tempStyle.isBorderFilled;
        colorKey = "isBorderFilled";
        break;
      case "border-width":
        tempStyle.defaultBorderWidth = Number(event.target.value);
        mapRef.setPaintProperty(
          layerId + "-line",
          "line-width",
          tempStyle.defaultBorderWidth
        );
        color = tempStyle.defaultBorderWidth;
        colorKey = "defaultBorderWidth";
        break;
      case "transparency":
        tempStyle.defaultOpacity = parseFloat(event.target.value / 100);
        mapRef.setPaintProperty(
          layerId,
          "fill-opacity",
          tempStyle.defaultOpacity
        );
        color = tempStyle.defaultOpacity;
        colorKey = "defaultOpacity";
        break;
      default:
        break;
    }
    setRadiusColorStyle({
      ...tempStyle,
    });
    dispatch(
      updateTradeArea({
        id: activeMarker.id,
        value: {
          radiusSetting: {
            order: showUpdateShading,
            [colorKey]: color,
          },
        },
      })
    );
  };

  const openColorSetting = (i) => {
    const lngLat = [activeMarker.latlng.lng, activeMarker.latlng.lat];
    setRadiusColorStyle({
      name: `circleData-${i}-${lngLat[0]}-${lngLat[1]}`,
      ...activeMarker.radiusSetting[i],
    });
    setShowUpdateShading(i);
  };

  const searchEmoji = debounce(async (e) => {
    setLoading(true);
    const value = e.target.value.trim();
    if (!value) {
      setLoading(false);
      return;
    }
    const res = await fetchEmojiIcons(value);
    setLoading(false);
    if (!res?.data) return;
    setIconNames(
      res.data
        .flatMap((d) => d.styles.map((x) => `fa-${x} fa-${d.id}`))
        // .splice(0, 50)
        .map((d) => ({ key: d }))
    );
  }, 1000);

  useEffect(() => {
    if (!allowRadiiUpdate) return;
    setEnableRadiusRing(Boolean(activeMarker.radiusEnable));
    const data = {};
    for (let i = 3; i >= 0; --i) {
      data[i] = {
        type: activeMarker.radiusSetting[i].type || "",
        size: activeMarker.radiusSetting[i].defaultRadius,
      };
    }
    setSelectedValue(data);
    setMarkerName(activeMarker.name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMarker]);

  return (
    <CustomDraggable
      width={340}
      height={showUpdateShading === -1 ? 400 : 260}
      headerTitle={
        showUpdateShading === -1 ? "Pointer Settings" : "Color Settings"
      }
      disableOverflow={showUpdateShading !== -1}
      customFooter={
        <Button
          sx={{ width: "20%", borderRadius: "0px", fontWeight: "bold" }}
          onClick={
            showUpdateShading === -1 ? onClose : () => setShowUpdateShading(-1)
          }
          variant="outlined"
        >
          Ok
        </Button>
      }
    >
      {showUpdateShading === -1 ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <PrimaryTextField
            label="Name"
            placeholder="Enter Name"
            type="text"
            fullWidth
            margin="normal"
            variant="outlined"
            value={markerName}
            onChange={(e) => setMarkerName(e.target.value)}
            onBlur={handleNameChange}
            sx={{
              "& .MuiOutlinedInput-input": {
                height: "auto",
              },
              "& .MuiOutlinedInput-root": {
                backgroundColor: "white",
                color: "black",
                borderRadius: "4px",
                "& fieldset": {
                  borderColor: "black", // default border
                },
                "&:hover fieldset": {
                  borderColor: "black", // on hover
                },
                "&.Mui-focused fieldset": {
                  borderColor: "black", // on focus
                },
              },
              "& .MuiInputLabel-root": {
                color: "black", // label color
              },
            }}
          />
          <Accordion
            disabled={!allowRadiiUpdate}
            defaultExpanded={Boolean(allowRadiiUpdate)}
            sx={{
              background: palette.background.light,
              color: palette.text.dark,
              border: `1px solid ${palette.primary.main}`,
              boxShadow: "none",
              borderRadius: "0px",
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon color="primary" />}>
              Radius Ring Settings
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <FormControlLabel
                  sx={{ color: palette.text.dark }}
                  control={
                    <Switch
                      checked={enableRadiusRing}
                      onChange={toggleRingRadius}
                    />
                  }
                  label={<span style={{ fontSize: 13 }}>Enable</span>}
                />
                {enableRadiusRing && (
                  <>
                    <RadiusRingSelector
                      index={0}
                      addRingRadiusLayer={addRingRadiusLayer}
                      selectedValue={selectedValue}
                      setSelectedValue={setSelectedValue}
                      removeDriveTime={removeDriveTime}
                      openColorSetting={openColorSetting}
                    />
                    <RadiusRingSelector
                      index={1}
                      addRingRadiusLayer={addRingRadiusLayer}
                      selectedValue={selectedValue}
                      setSelectedValue={setSelectedValue}
                      removeDriveTime={removeDriveTime}
                      openColorSetting={openColorSetting}
                    />
                    <RadiusRingSelector
                      index={2}
                      addRingRadiusLayer={addRingRadiusLayer}
                      selectedValue={selectedValue}
                      setSelectedValue={setSelectedValue}
                      removeDriveTime={removeDriveTime}
                      openColorSetting={openColorSetting}
                    />
                    <RadiusRingSelector
                      index={3}
                      addRingRadiusLayer={addRingRadiusLayer}
                      selectedValue={selectedValue}
                      setSelectedValue={setSelectedValue}
                      removeDriveTime={removeDriveTime}
                      openColorSetting={openColorSetting}
                    />
                  </>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
          <Accordion
            sx={{
              background: palette.background.light,
              color: palette.text.dark,
              border: `1px solid ${palette.primary.main}`,
              boxShadow: "none",
              borderRadius: "0px",
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon color="primary" />}>
              Styling
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <ColorPicker
                  noCheckbox
                  title={"Pointer Color"}
                  color={activeMarker.pointer.color}
                  setColor={handleStyleChange}
                  type={"pointerColor"}
                />
                {/* <TextField
                  type="number"
                  sx={{ width: "65px" }}
                  label="Size"
                  defaultValue={activeMarker.pointer.size}
                  slotProps={{ input: { max: 30, min: 14 } }}
                  onChange={(e) => handleStyleChange("size", e.target.value)}
                  // defaultValue={polygonStyleRef.current.polygonBorderWidth}
                  // onChange={(e) => changeStyle("border-width", e)}
                /> */}
                <Typography sx={{ color: palette.text.dark, fontSize: 15 }}>
                  Pointer Size:
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    width: "100%",
                  }}
                >
                  <Slider
                    min={14}
                    step={1}
                    max={30}
                    defaultValue={activeMarker.pointer.size}
                    valueLabelDisplay="auto"
                    onChangeCommitted={(_, val) =>
                      handleStyleChange("size", val)
                    }
                    sx={{ width: "85%" }}
                  />
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Typography
                    sx={{
                      color: palette.text.dark,
                      fontSize: 15,
                    }}
                  >
                    Change Marker
                  </Typography>
                  <PrimaryTextField
                    placeholder="Search Emoji"
                    onChange={searchEmoji}
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "white",
                        color: "black",
                        borderRadius: "4px",
                        "& fieldset": {
                          borderColor: "black", // default border
                        },
                        "&:hover fieldset": {
                          borderColor: "black", // on hover
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "black", // on focus
                        },
                      },
                      "& .MuiInputLabel-root": {
                        color: "black", // label color
                      },
                    }}
                  />
                  <Box
                    sx={{
                      height: 110,
                      width: "100%",
                    }}
                  >
                    {loading ? (
                      <Box
                        sx={{
                          height: "inherit",
                          justifySelf: "center",
                          alignContent: "center",
                        }}
                      >
                        <CircularProgress />
                      </Box>
                    ) : iconNames.length ? (
                      <Box
                        sx={{
                          overflow: "auto",
                          flexWrap: "wrap",
                          height: "inherit",
                        }}
                      >
                        {iconNames.map((d, i) => {
                          return (
                            <IconButton
                              key={i}
                              sx={{
                                p: 1,
                                height: 37,
                                width: 40,
                                border: `1px solid ${palette.primary.main}`,
                                borderRadius: 1,
                                m: "4px",
                                ...(activeEmoji === d.key
                                  ? {
                                      background: palette.primary.main,
                                      color: palette.text.dark,
                                    }
                                  : {
                                      background: palette.background.light,
                                      color: palette.text.dark,
                                    }),
                              }}
                              onClick={() => handleEmojiChange(d.key)}
                            >
                              <i
                                className={d.key}
                                style={{ fontSize: "17px" }}
                              />
                            </IconButton>
                          );
                        })}
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          height: "inherit",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Typography
                          sx={{ color: palette.text.dark, fontSize: 18 }}
                        >
                          No Emojis
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <ColorPicker
            title={"Fill"}
            checked={!radiusColorStyle.isTransparent}
            color={radiusColorStyle.defaultRingRadiusColor}
            setColor={changeStyle}
            type={"background-color"}
          />
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              flexWrap: "wrap",
            }}
          >
            <ColorPicker
              title={"Border"}
              checked={!radiusColorStyle.isBorderTransparent}
              color={radiusColorStyle.defaultRingRadiusBorder}
              setColor={changeStyle}
              type={"border-color"}
            />
            <TextField
              type="number"
              sx={{
                width: "65px",
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "white",
                  color: "black",
                  borderRadius: "4px",
                  "& fieldset": {
                    borderColor: "black", // default border
                  },
                  "&:hover fieldset": {
                    borderColor: "black", // on hover
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "black", // on focus
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "black", // label color
                },
              }}
              label="Size"
              defaultValue={radiusColorStyle.defaultBorderWidth}
              onChange={(e) => changeStyle("border-width", e)}
            />
          </Box>
          <Typography sx={{ color: palette.text.dark, fontSize: 15 }}>
            Transparency:
          </Typography>
          <Box
            sx={{ display: "flex", justifyContent: "center", width: "100%" }}
          >
            <Slider
              min={0}
              step={1}
              max={100}
              defaultValue={radiusColorStyle.defaultOpacity * 100}
              valueLabelDisplay="auto"
              onChange={(e) => changeStyle("transparency", e)}
              sx={{ width: "85%" }}
            />
          </Box>
        </Box>
      )}
    </CustomDraggable>
  );
};
